import React from "react";
import { cn } from "../../../utils/util";
import styles from "./geometry.module.css";

/**
 * Geometry component that renders basic geometric shapes
 * 
 * @param {Object} props
 * @param {'circle'|'triangle'|'square'} props.shape - The geometric shape to render
 * @param {string|number} props.size - Size of the shape in px (default: 24)
 * @param {string} props.color - Fill color of the shape
 * @param {string} props.borderColor - Border color of the shape
 * @param {string|number} props.borderWidth - Border width in px
 * @param {number} props.rotation - Rotation in degrees
 * @param {string} props.className - Additional CSS class
 * @param {Object} props.style - Additional inline styles
 */
const Geometry = ({
  shape = "circle",
  size = 24,
  color = "#FF6500",
  borderColor,
  borderWidth = 0,
  rotation = 0,
  className,
  style = {},
  ...props
}) => {
  // Convert size to number if it's a string
  const sizeValue = typeof size === "string" ? parseInt(size, 10) : size;
  
  // Base styles for all shapes
  const baseStyles = {
    width: `${sizeValue}px`,
    height: `${sizeValue}px`,
    backgroundColor: color,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    border: borderWidth ? `${borderWidth}px solid ${borderColor || color}` : undefined,
    ...style
  };

  // Determine which shape class to use
  const shapeClass = styles[shape] || styles.circle;

  return (
    <div
      className={cn(styles.geometry, shapeClass, className)}
      style={baseStyles}
      role="presentation"
      aria-hidden="true"
      {...props}
    />
  );
};

// Convenience components for specific shapes
const Circle = (props) => <Geometry shape="circle" {...props} />;
const Triangle = (props) => <Geometry shape="triangle" {...props} />;
const Square = (props) => <Geometry shape="square" {...props} />;

// Attach convenience components as properties
Geometry.Circle = Circle;
Geometry.Triangle = Triangle;
Geometry.Square = Square;

export default Geometry; 